<template>
    <div class="industry-distribution">
        <div ref="industryChart" class="chart-container"></div>
        <DashboardLoading
            :visible="initLoading && loading"
            text="加载部署行业分布数据"
            size="normal"
        />
    </div>
</template>

<script>
import * as echarts from 'echarts'
import { getSceneDeviceStats } from '@/api/dashboard'
import DashboardLoading from './DashboardLoading.vue'

export default {
  name: 'IndustryDistribution',
  components: {
    DashboardLoading
  },
  props: {
    fontSizes: {
      type: Object,
      default: () => ({
        titleFontSize: 16,
        legendFontSize: 12,
        axisLabelFontSize: 10,
        axisNameFontSize: 12,
        tooltipFontSize: 14,
        dataLabelFontSize: 11,
        smallFontSize: 8,
        largeFontSize: 18
      })
    }
  },
  data () {
    return {
      chartInstance: null,
      refreshTimer: null,
      loading: false,
      sceneData: null,
      summaryData: null,
      industryData: [
        { name: '学校', value: 169 },
        { name: '医院', value: 139 },
        { name: '政务窗口', value: 96 },
        { name: '企业', value: 89 },
        { name: '小区/便民店', value: 83 },
        { name: '酒店', value: 65 }
      ],
      initLoading: true
    }
  },
  mounted () {
    this.fetchSceneDeviceStats()
    // 每10分钟刷新一次数据
    this.refreshTimer = setInterval(() => {
      console.log('重新初始化部署行业分布数据', this.$moment(new Date(), 'YYYY-MM-DD HH:mm:ss'))
      this.fetchSceneDeviceStats()
    }, 600000)
  },
  beforeDestroy () {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    this.destroyChart()
  },
  methods: {
    destroyChart () {
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  },
  watch: {
    fontSizes: {
      handler () {
        if (this.chartInstance) {
          this.updateChartOption()
        }
      },
      deep: true
    }
  },
  methods: {
    async fetchSceneDeviceStats () {
      try {
        this.loading = true
        const response = await getSceneDeviceStats()
        console.log('部署行业分布数据:', response)

        if (response.data.status && response.data.data && response.data.data.sceneStats) {
          // 将API数据转换为组件需要的格式
          this.sceneData = response.data.data.sceneStats.map(item => ({
            name: item.sceneName,
            value: item.counts
          }))
          this.summaryData = response.data.data.summary

          // 数据获取成功后初始化图表
          this.initChart()
        } else {
          console.error('获取部署行业分布数据失败:', response)
          // 如果API失败，使用模拟数据初始化图表
          this.initChart()
        }
      } catch (error) {
        console.error('获取部署行业分布数据出错:', error)
        // 如果出错，使用模拟数据初始化图表
        this.initChart()
      } finally {
        this.loading = false
        this.initLoading = false
      }
    },

    getChartData () {
      // 如果有API数据，使用API数据；否则使用模拟数据
      return this.sceneData && this.sceneData.length > 0 ? this.sceneData : this.industryData
    },

    // 生成随机的好看颜色
    generateBeautifulColors (count) {
      // 预定义的美观颜色调色板

      // 高质量的单色调色板
      const qualityColors = [
        '#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe',
        '#ffecd2', '#fcb69f', '#a8edea', '#fed6e3', '#d299c2', '#fef9d7',
        '#ff9a9e', '#fecfef', '#667eea', '#764ba2', '#f093fb', '#f5576c',
        '#4facfe', '#00f2fe', '#ffecd2', '#fcb69f', '#a8edea', '#fed6e3'
      ]

      // 如果需要的颜色数量较少，使用预定义的高质量颜色
      if (count <= qualityColors.length) {
        return qualityColors.slice(0, count)
      }

      // 如果需要更多颜色，生成随机的HSL颜色
      const colors = []
      const hueStep = 360 / count

      for (let i = 0; i < count; i++) {
        const hue = (i * hueStep + Math.random() * 30) % 360
        const saturation = 65 + Math.random() * 25 // 65-90%
        const lightness = 55 + Math.random() * 20 // 55-75%
        colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`)
      }

      return colors
    },

    initChart () {
      this.$nextTick(() => {
        const chartContainer = this.$refs.industryChart
        if (!chartContainer) return

        // 销毁已存在的图表实例
        if (this.chartInstance) {
          this.chartInstance.dispose()
          this.chartInstance = null
        }

        // 初始化图表
        this.chartInstance = echarts.init(chartContainer)

        // 获取图表数据
        const chartData = this.getChartData()

        // 计算总数
        const total = chartData.reduce((sum, item) => sum + item.value, 0)

        // 饼图位置 - 调整为居中并向下移动以适应顶部图例
        const pieCenter = ['50%', '65%']

        // 生成动态颜色
        const dynamicColors = this.generateBeautifulColors(chartData.length)

        // 配置项
        const option = {
          backgroundColor: 'transparent',
          color: dynamicColors,
          tooltip: {
            show: true,
            backgroundColor: 'rgba(0, 30, 80, 0.9)',
            borderColor: '#0096ff',
            borderWidth: 1,
            textStyle: {
              color: '#b8d4f0',
              fontSize: this.fontSizes.tooltipFontSize
            },
            formatter: function (params) {
              const percentage = ((params.value / total) * 100).toFixed(1)
              return `
                                <div style="padding: 8px;">
                                    <div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                                    <div>设备数量: <span style="color: ${params.color};">${params.value}台</span></div>
                                    <div>占比: <span style="color: ${params.color};">${percentage}%</span></div>
                                </div>
                            `
            },
            extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
          },
          legend: {
            show: true,
            orient: 'horizontal',
            textStyle: {
              color: '#b8d4f0',
              fontSize: this.fontSizes.legendFontSize - 4
            },
            left: 'center',
            top: '-30',
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 15,
            formatter: (name) => {
              // 只显示行业名称，不显示具体数字
              return name
            }
          },
          series: [
            // 背景装饰0 实心白圆 层级最高zlevel: 4
            {
              type: 'pie',
              zlevel: 4,
              radius: ['0%', '7%'],
              center: pieCenter,
              silent: true,
              clockwise: false,
              label: {
                show: false
              },
              data: [{
                name: null,
                value: 0,
                itemStyle: {
                  color: '#FFF'
                }
              }]
            },
            // 背景装饰1 半透明圆 zlevel: 3 层级第二
            {
              type: 'pie',
              radius: ['0%', '15%'],
              center: pieCenter,
              zlevel: 3,
              silent: true,
              clockwise: false,
              label: {
                show: false
              },
              data: [{
                name: null,
                value: 0,
                itemStyle: {
                  color: 'rgba(255,255,255, 0.5)'
                }
              }]
            },
            // 背景装饰3 半透明圆 zlevel: 1 层级最低 作为底盘
            {
              type: 'pie',
              zlevel: 1,
              radius: ['60%', '0%'],
              center: pieCenter,
              silent: true,
              clockwise: false,
              label: {
                show: false
              },
              data: [{
                name: null,
                value: 0,
                itemStyle: {
                  color: 'rgba(255,255,255, 0.1)'
                }
              }]
            },
            // 数据源 - 玫瑰图
            {
              type: 'pie',
              // roseType: true 和 roseType: 'area' 在填充值后显示方式不同
              roseType: true,
              clockwise: false,
              center: pieCenter,
              zlevel: 2,
              radius: ['4%', '60%'], // 第一个参数控制玫瑰图圆心大小,第二个参数控制玫瑰图大小
              itemStyle: {
                borderRadius: 4
              },
              data: chartData,
              label: {
                show: true,
                color: '#b8d4f0',
                fontSize: this.fontSizes.dataLabelFontSize - 4,
                formatter: function (params) {
                  return `${params.name} ${params.value}台`
                }
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 150, 255, 0.5)'
                }
              },
              animationType: 'scale',
              animationEasing: 'elasticOut',
              animationDelay: function (idx) {
                return Math.random() * 200
              }
            }
          ]
        }

        // 设置配置项并渲染图表
        this.chartInstance.setOption(option)

        // 响应式调整
        this.resizeHandler = () => {
          if (this.chartInstance && !this.chartInstance.isDisposed()) {
            this.chartInstance.resize()
          }
        }

        window.addEventListener('resize', this.resizeHandler)
      })
    },

    updateChartOption () {
      if (!this.chartInstance || this.chartInstance.isDisposed()) {
        this.initChart()
        return
      }

      // 获取图表数据
      const chartData = this.getChartData()

      // 计算总数
      const total = chartData.reduce((sum, item) => sum + item.value, 0)

      // 饼图位置 - 调整为居中并向下移动以适应顶部图例
      const pieCenter = ['50%', '65%']

      // 生成动态颜色
      const dynamicColors = this.generateBeautifulColors(chartData.length)

      // 配置项
      const option = {
        backgroundColor: 'transparent',
        color: dynamicColors,
        tooltip: {
          show: true,
          backgroundColor: 'rgba(0, 30, 80, 0.9)',
          borderColor: '#0096ff',
          borderWidth: 1,
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.tooltipFontSize
          },
          formatter: function (params) {
            const percentage = ((params.value / total) * 100).toFixed(1)
            return `
                            <div style="padding: 8px;">
                                <div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                                <div>设备数量: <span style="color: ${params.color};">${params.value}台</span></div>
                                <div>占比: <span style="color: ${params.color};">${percentage}%</span></div>
                            </div>
                        `
          },
          extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
        },
        legend: {
          show: true,
          orient: 'horizontal',
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.legendFontSize
          },
          left: 'center',
          top: '-10',
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 15,
          formatter: (name) => {
            // 只显示行业名称，不显示具体数字
            return name
          }
        },
        series: [
          // 背景装饰0 实心白圆 层级最高zlevel: 4
          {
            type: 'pie',
            zlevel: 4,
            radius: ['0%', '7%'],
            center: pieCenter,
            silent: true,
            clockwise: false,
            label: {
              show: false
            },
            data: [{
              name: null,
              value: 0,
              itemStyle: {
                color: '#FFF'
              }
            }]
          },
          // 背景装饰1 半透明圆 zlevel: 3 层级第二
          {
            type: 'pie',
            radius: ['0%', '15%'],
            center: pieCenter,
            zlevel: 3,
            silent: true,
            clockwise: false,
            label: {
              show: false
            },
            data: [{
              name: null,
              value: 0,
              itemStyle: {
                color: 'rgba(255,255,255, 0.5)'
              }
            }]
          },
          // 背景装饰3 半透明圆 zlevel: 1 层级最低 作为底盘
          {
            type: 'pie',
            zlevel: 1,
            radius: ['60%', '0%'],
            center: pieCenter,
            silent: true,
            clockwise: false,
            label: {
              show: false
            },
            data: [{
              name: null,
              value: 0,
              itemStyle: {
                color: 'rgba(255,255,255, 0.1)'
              }
            }]
          },
          // 数据源 - 玫瑰图
          {
            type: 'pie',
            // roseType: true 和 roseType: 'area' 在填充值后显示方式不同
            roseType: true,
            clockwise: false,
            center: pieCenter,
            zlevel: 2,
            radius: ['4%', '60%'], // 第一个参数控制玫瑰图圆心大小,第二个参数控制玫瑰图大小
            itemStyle: {
              borderRadius: 4
            },
            data: chartData,
            label: {
              show: true,
              color: '#b8d4f0',
              fontSize: this.fontSizes.dataLabelFontSize,
              formatter: function (params) {
                return `${params.name}\n${params.value}台`
              }
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 150, 255, 0.5)'
              }
            },
            animationType: 'scale',
            animationEasing: 'elasticOut',
            animationDelay: function (idx) {
              return Math.random() * 200
            }
          }
        ]
      }

      this.chartInstance.setOption(option, true)
    }
  }
}
</script>

<style scoped>
.industry-distribution {
    width: 100%;
    height: 100%;
}

.chart-container {
    width: 100%;
    height: 100%;
    min-height: 250px;
}

/* 大屏优化样式 */
@media (min-width: 1920px) {
    .chart-container {
        min-height: 300px;
    }
}

@media (min-width: 2560px) {
    .chart-container {
        min-height: 400px;
    }
}
</style>
